import { StaticImageData } from 'next/image'
import { mainnet, bsc, polygonMumbai, bscTestnet, polygon } from 'wagmi/chains'
import { BSC_CHAIN_ID, ETHEREUM_CHAIN_ID, POLYGON_CHAIN_ID } from '..'

import {
  EthBssb,
  EthDaii,
  EthDova,
  EthNhub,
  FakeGoerliNhub,
  PolygonMubi,
  PolygonOrdi,
  PolygonRats,
} from './erc20Tokens'
import OrdiSvg from '@/assets/imgs/token/ordi.svg'
import OxbtSvg from '@/assets/imgs/token/oxbt.svg'
import Usd0Svg from '@/assets/imgs/token/usd0.svg'
import IgliSvg from '@/assets/imgs/token/igli.svg'
import WheeJpeg from '@/assets/imgs/token/whee.jpeg'
import ChaxPng from '@/assets/imgs/token/chax.png'
import BchaxPng from '@/assets/imgs/token/bchax.png'
import MubiSvg from '@/assets/imgs/token/mubi.svg'
import SatsPng from '@/assets/imgs/token/sats.png'
import TurtPng from '@/assets/imgs/token/turt.png'
import B2bbPng from '@/assets/imgs/token/b2bb.png'
import FooxJpg from '@/assets/imgs/token/foox.jpg'
import RatsJpg from '@/assets/imgs/token/rats.jpg'
import NhubPng from '@/assets/imgs/token/nhub.png'
import BssbSvg from '@/assets/imgs/token/bssb.svg'
import DaiiSvg from '@/assets/imgs/token/daii.svg'
import DovaJpg from '@/assets/imgs/token/dova.jpg'
// import TrspJpg from '@/assets/imgs/token/trsp.jpg'

export type SupportedTicker =
  | 'ordi'
  | 'OXBT'
  | 'sats'
  | 'USD0'
  | 'igli'
  | 'WHEE'
  | 'chax'
  | 'MUBI'
  | 'TURT'
  | 'b2bb'
  | 'foox'
  | 'rats'
  | 'NHUB'
  | 'BSSB'
  | 'DAII'
  | 'DOVA'
// | 'trsp'

export interface Erc20Token {
  symbol: string
  decimal: number
  address: `0x${string}`
}

export type SwapName = 'uniswap' | 'pancake'
export interface SwapInfo {
  url: string
  name: SwapName
}

export interface Ticker {
  name: SupportedTicker
  logoSrcSet: { brc: StaticImageData; erc?: StaticImageData }
  supportChains: number[]
  erc20Tokens: Record<number, Erc20Token>
  coinGeckoId?: string
  disabled?: boolean
  swapInfo?: SwapInfo[]
}

const ORDI: Ticker = {
  name: 'ordi',
  logoSrcSet: { brc: OrdiSvg },
  supportChains: [ETHEREUM_CHAIN_ID, BSC_CHAIN_ID, POLYGON_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'ordi',
      decimal: 18,
      address: '0xe2aE1a99bBd2eFab0a5C38f2146B7aCE61aBC5cE',
    },
    [bsc.id]: {
      symbol: 'ordi',
      decimal: 18,
      address: '0xe2aE1a99bBd2eFab0a5C38f2146B7aCE61aBC5cE',
    },
    [polygonMumbai.id]: {
      symbol: 'ordi',
      decimal: 18,
      address: '0x5Cc594c71D55C2b4e17069d436C6b9BF22D6D862',
    },
    [bscTestnet.id]: {
      symbol: 'ordi',
      decimal: 18,
      address: '0x5Cc594c71D55C2b4e17069d436C6b9BF22D6D862',
    },
    [polygon.id]: PolygonOrdi,
  },
  coinGeckoId: 'ordinals',
  swapInfo: [
    { name: 'uniswap', url: 'https://app.uniswap.org/tokens/ethereum/0xe2ae1a99bbd2efab0a5c38f2146b7ace61abc5ce' },
  ],
}
const OXBT: Ticker = {
  name: 'OXBT',
  logoSrcSet: { brc: OxbtSvg },
  supportChains: [ETHEREUM_CHAIN_ID, BSC_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'OXBT',
      decimal: 18,
      address: '0xc41987DAce973DDd2Bbbb007FDd6d41038Cf4F5E',
    },
    [bsc.id]: {
      symbol: 'OXBT',
      decimal: 18,
      address: '0xc41987DAce973DDd2Bbbb007FDd6d41038Cf4F5E',
    },
    [polygonMumbai.id]: {
      symbol: 'OXBT',
      decimal: 18,
      address: '0xE4F03d84387FC6bCa0448a029F622E9BB31B0337',
    },
    [bscTestnet.id]: {
      symbol: 'OXBT',
      decimal: 18,
      address: '0xE4F03d84387FC6bCa0448a029F622E9BB31B0337',
    },
  },
  coinGeckoId: 'oxbt',
  swapInfo: [
    { name: 'uniswap', url: 'https://app.uniswap.org/tokens/ethereum/0xc41987DAce973DDd2Bbbb007FDd6d41038Cf4F5E' },
  ],
}

export const USDT_TICKER: Ticker = {
  name: 'USD0',
  logoSrcSet: { brc: Usd0Svg },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'USDT',
      decimal: 6,
      address: '0xdac17f958d2ee523a2206206994597c13d831ec7',
    },
    [polygonMumbai.id]: {
      symbol: 'USDT',
      decimal: 6,
      address: '0x96d9512E0AD19891485689140a52d22e3fa87ab6',
    },
  },
  coinGeckoId: 'tether',
}
const igli: Ticker = {
  name: 'igli',
  logoSrcSet: { brc: IgliSvg },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'igli',
      decimal: 18,
      address: '0xCc6773F710b7F2A2357e228609c114f8FAe04B61',
    },
    [polygonMumbai.id]: {
      symbol: 'igli',
      decimal: 18,
      address: '0xB164c5844B67e141Ba2f682f4C0d4C5CeA4bcdCF',
    },
  },
  swapInfo: [
    { name: 'uniswap', url: 'https://app.uniswap.org/tokens/ethereum/0xCc6773F710b7F2A2357e228609c114f8FAe04B61' },
  ],
}
const WHEE: Ticker = {
  name: 'WHEE',
  logoSrcSet: { brc: WheeJpeg },
  supportChains: [ETHEREUM_CHAIN_ID, BSC_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'WHEE',
      decimal: 18,
      address: '0x2512c545A0117353e4E9aCDdfF860342640d3CF6',
    },
    [polygonMumbai.id]: {
      symbol: 'WHEE',
      decimal: 18,
      address: '0x971A2A5Ba078b1fD54C019F57Ca278C05E359d47',
    },
    [bsc.id]: {
      symbol: 'WHEE',
      decimal: 18,
      address: '0x2512c545A0117353e4E9aCDdfF860342640d3CF6',
    },
    [bscTestnet.id]: {
      symbol: 'WHEE',
      decimal: 18,
      address: '0x971A2A5Ba078b1fD54C019F57Ca278C05E359d47',
    },
  },
  coinGeckoId: 'whee',
  swapInfo: [
    {
      name: 'uniswap',
      url: 'https://app.uniswap.org/tokens/ethereum/0x2512c545A0117353e4E9aCDdfF860342640d3CF6',
    },
  ],
}
const chax: Ticker = {
  name: 'chax',
  logoSrcSet: { brc: ChaxPng, erc: BchaxPng },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'bCHAX',
      decimal: 18,
      address: '0xccC9fEe3011d4437e93184a9347eaAa838304c79',
    },
    [polygonMumbai.id]: {
      symbol: 'bCHAX',
      decimal: 18,
      address: '0x18BE10840d5bCDe9A0fEd93F271cEC5d2a0e6B17',
    },
  },
  coinGeckoId: 'chax',
}

export const MUBI: Ticker = {
  name: 'MUBI',
  logoSrcSet: { brc: MubiSvg },
  supportChains: [ETHEREUM_CHAIN_ID, BSC_CHAIN_ID, POLYGON_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'MUBI',
      decimal: 18,
      address: '0x38e382F74dfb84608F3C1F10187f6bEf5951DE93',
    },
    [polygonMumbai.id]: {
      symbol: 'MUBI',
      decimal: 18,
      address: '0xf31D448fA81AD94b68b50A2eBED68fb325a329aF',
    },
    [bsc.id]: {
      symbol: 'MUBI',
      decimal: 18,
      address: '0x38e382F74dfb84608F3C1F10187f6bEf5951DE93',
    },
    [bscTestnet.id]: {
      symbol: 'MUBI',
      decimal: 18,
      address: '0x38e382F74dfb84608F3C1F10187f6bEf5951DE93',
    },
    [polygon.id]: PolygonMubi,
  },
  swapInfo: [
    {
      url: 'https://app.uniswap.org/tokens/ethereum/0x38e382F74dfb84608F3C1F10187f6bEf5951DE93',
      name: 'uniswap',
    },
  ],
  coinGeckoId: 'multibit',
}

const sats: Ticker = {
  name: 'sats',
  logoSrcSet: { brc: SatsPng },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'sats',
      decimal: 18,
      address: '0x143D7A700a533B4baF6D693449b278A8A2F5927d',
    },
    [polygonMumbai.id]: {
      symbol: 'sats',
      decimal: 18,
      address: '0xbB35adc993463B78133cb8770cB3E6B235e930b1',
    },
  },
  coinGeckoId: 'sats-ordinals',
  swapInfo: [
    {
      url: 'https://app.uniswap.org/tokens/ethereum/0x143D7A700a533B4baF6D693449b278A8A2F5927d',
      name: 'uniswap',
    },
  ],
}

const TURT: Ticker = {
  name: 'TURT',
  logoSrcSet: { brc: TurtPng },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'TURT',
      decimal: 18,
      address: '0x0800394f6E23dD539929c8B77a3d45c96F76AEFC',
    },
    [polygonMumbai.id]: {
      symbol: 'TURT',
      decimal: 18,
      address: '0xAF7813828B44051d830aA1c03c326b5aA97D3B80',
    },
  },
  coinGeckoId: 'turtsat',
  swapInfo: [
    {
      name: 'uniswap',
      url: 'https://app.uniswap.org/tokens/ethereum/0x0800394f6E23dD539929c8B77a3d45c96F76AEFC',
    },
  ],
}

const b2bb: Ticker = {
  name: 'b2bb',
  logoSrcSet: { brc: B2bbPng },
  supportChains: [BSC_CHAIN_ID],
  erc20Tokens: {
    [bsc.id]: {
      symbol: 'B2B',
      decimal: 18,
      address: '0xa85128073e25a0190b86b72e3976f8b02dad757d',
    },
    [bscTestnet.id]: {
      symbol: 'B2B',
      decimal: 18,
      address: '0x451cAB1Ffb7D81968cfbb93BbB5E3a7c576992f1',
    },
  },
  coinGeckoId: 'b2b-token',
  swapInfo: [
    {
      url: 'https://pancakeswap.finance/swap?outputCurrency=0xA85128073e25a0190b86b72E3976F8B02dAD757D',
      name: 'pancake',
    },
  ],
}

const foox: Ticker = {
  name: 'foox',
  logoSrcSet: { brc: FooxJpg },
  supportChains: [ETHEREUM_CHAIN_ID /* , BSC_CHAIN_ID */],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'foox',
      decimal: 18,
      address: '0x20fCefA41045080764C48C2b9429e44C644e5deA',
    },
    [polygonMumbai.id]: {
      symbol: 'foox',
      decimal: 18,
      address: '0x466A9bEe2a2dCd0260Ee7A211AD3023de2e8DA51',
    },
    // [bsc.id]: {
    //   symbol: 'foox',
    //   decimal: 18,
    //   address: '0x20fCefA41045080764C48C2b9429e44C644e5deA',
    // },
    // [bscTestnet.id]: {
    //   symbol: 'foox',
    //   decimal: 18,
    //   address: '0x20fCefA41045080764C48C2b9429e44C644e5deA', // fake
    // },
  },
}

const rats: Ticker = {
  name: 'rats',
  logoSrcSet: { brc: RatsJpg },
  supportChains: [ETHEREUM_CHAIN_ID, POLYGON_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: {
      symbol: 'rats',
      decimal: 18,
      address: '0xeb55aD3F84C75960aBa35143BC7128F0BCdfc0ef',
    },
    [polygonMumbai.id]: {
      symbol: 'rats',
      decimal: 18,
      address: '0x98209d90dba3d22d67d9E8718891cE7158B98b26',
    },
    [polygon.id]: PolygonRats,
  },
  coinGeckoId: 'rats',
  swapInfo: [
    {
      name: 'uniswap',
      url: 'https://app.uniswap.org/tokens/ethereum/0xeb55aD3F84C75960aBa35143BC7128F0BCdfc0ef',
    },
  ],
}

const NHUB: Ticker = {
  name: 'NHUB',
  logoSrcSet: { brc: NhubPng },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: EthNhub,
    [polygonMumbai.id]: FakeGoerliNhub,
  },
  swapInfo: [
    { name: 'uniswap', url: 'https://app.uniswap.org/tokens/ethereum/0xB259fcF928e58E39D381704d3cAe9B8e969814A0' },
  ],
}

const BSSB: Ticker = {
  name: 'BSSB',
  logoSrcSet: { brc: BssbSvg },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: EthBssb,
    [polygonMumbai.id]: EthBssb, // fake
  },
  swapInfo: [
    { name: 'uniswap', url: 'https://app.uniswap.org/tokens/ethereum/0xda31D0d1Bc934fC34F7189E38A413ca0A5e8b44F' },
  ],
}

const DAII: Ticker = {
  name: 'DAII',
  logoSrcSet: { brc: DaiiSvg },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: EthDaii,
    [polygonMumbai.id]: EthDaii, // fake
  },
}

const DOVA: Ticker = {
  name: 'DOVA',
  logoSrcSet: { brc: DovaJpg },
  supportChains: [ETHEREUM_CHAIN_ID],
  erc20Tokens: {
    [mainnet.id]: EthDova,
    [polygonMumbai.id]: EthDova, // fake
  },
}

// const trsp: Ticker = {
//   name: 'trsp',
//   logoSrcSet: { brc: TrspJpg },
//   supportChains: [ETHEREUM_CHAIN_ID],
//   erc20Tokens: {
//     [mainnet.id]: {
//       symbol: 'trsp',
//       decimal: 18,
//       address: '0x9BBFf9c6fa051484e8d90Da2B15Ad21763E02800', // fake
//     },
//     [polygonMumbai.id]: {
//       symbol: 'trsp',
//       decimal: 18,
//       address: '0x9BBFf9c6fa051484e8d90Da2B15Ad21763E02800',
//     },
//   },
// }

export const TICKERS: Ticker[] = [
  MUBI,
  sats,
  b2bb,
  ORDI,
  BSSB,
  DAII,
  DOVA,
  rats,
  NHUB,
  TURT,
  OXBT,
  WHEE,
  chax,
  foox,
  igli,
  USDT_TICKER,
]

export const TICKERS_MAP_BY_NAME: Partial<Record<SupportedTicker, Ticker>> = {}
TICKERS.forEach((ticker) => {
  TICKERS_MAP_BY_NAME[ticker.name] = ticker
})

export const TICKERS_FILTER_BY_CHAIN: Record<number, Ticker[]> = {}
TICKERS.forEach((ticker) => {
  ticker.supportChains.forEach((chain) => {
    if (!TICKERS_FILTER_BY_CHAIN[chain]) {
      TICKERS_FILTER_BY_CHAIN[chain] = []
    }
    TICKERS_FILTER_BY_CHAIN[chain].push(ticker)
  })
})

export const RECOMMENDED_TICKERS: Record<number, Ticker[]> = {
  [ETHEREUM_CHAIN_ID]: [MUBI, ORDI, sats],
  [BSC_CHAIN_ID]: [MUBI, ORDI],
  [POLYGON_CHAIN_ID]: [MUBI, ORDI, rats],
}
